import React, { useEffect, useState } from "react";
import moment from "moment";
import { Icon } from "@iconify/react";
import { enqueueSnackbar } from "notistack";
import getBusinessChangeRequests from "../../../controllers/user/getBusinessChangeRequests";

const FIELD_LABELS = {
  CACCertificateDoc: "CAC certificate",
  CACCO2Doc: "CAC status report (CO2)",
  CACC07Doc: "CAC particulars of directors (CO7)",
  identityVerificationDoc: "Director's ID",
  utilityBill: "Utility bill",
  tradingName: "Trading name",
  agencyName: "Agency name",
  businessEmail: "Business email",
  businessPhone: "Business phone",
  registeredBusinessName: "Registered business name",
  companyNumber: "RC number",
  taxIdentification: "TIN",
};

function fieldLabel(field) {
  return (
    FIELD_LABELS[field] ||
    String(field || "Field")
      .replace(/([A-Z])/g, " $1")
      .trim()
  );
}

function statusTone(status) {
  const s = String(status || "").toLowerCase();
  if (s === "pending" || s === "submitted" || s === "in_review" || s === "in-review") {
    return "bg-[#FFF8EE] text-[#7A4A08] border-[#F0D3A6]";
  }
  if (s === "approved" || s === "accepted") {
    return "bg-emerald-50 text-emerald-800 border-emerald-200";
  }
  if (s === "rejected" || s === "declined") {
    return "bg-[#FDF2F2] text-[#A81E27] border-[#F0C2C4]";
  }
  return "bg-[#F8FAFC] text-[#5A6478] border-[#E3E8F0]";
}

function requestTime(row) {
  return row?.submittedAt || row?.createdAt || row?.updatedAt;
}

/**
 * History of all business-change / verification requests for this account.
 */
export default function CustomerDetailHistorySection({ data, onOpenRequest }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!data?._id) {
        setRows([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      const res = await getBusinessChangeRequests(data._id);
      if (cancelled) return;
      setLoading(false);
      if (!res?.return) {
        setRows([]);
        enqueueSnackbar(res?.msg || "Couldn't load request history.", {
          variant: "error",
        });
        return;
      }
      const list = Array.isArray(res.data) ? [...res.data] : [];
      list.sort((a, b) => {
        const ta = new Date(requestTime(a) || 0).getTime();
        const tb = new Date(requestTime(b) || 0).getTime();
        return tb - ta;
      });
      setRows(list);
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [data?._id]);

  if (loading) {
    return (
      <div className="flex flex-col gap-2 p-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="h-16 animate-pulse rounded-xl border border-[#EEF1F6] bg-[#F8FAFC]"
          />
        ))}
      </div>
    );
  }

  if (!rows.length) {
    return (
      <div className="px-4 py-10 text-center text-sm text-[#6D7C94]">
        No business change requests yet for this account.
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 p-3 sm:p-4">
      <p className="m-0 px-1 text-[12px] text-[#6D7C94]">
        All verification and business-change requests for this agency.
      </p>
      <div className="overflow-hidden rounded-xl border border-[#E3E8F0] bg-white">
        {rows.map((row) => {
          const id = row?._id || row?.id;
          const changes = Array.isArray(row?.changes) ? row.changes : [];
          const summary = changes
            .map((c) => fieldLabel(c?.field))
            .filter(Boolean)
            .slice(0, 3)
            .join(", ");
          const more = changes.length > 3 ? ` +${changes.length - 3}` : "";
          const when = requestTime(row);

          return (
            <div
              key={id}
              className="flex flex-wrap items-center gap-3 border-b border-[#EEF1F6] px-4 py-3 last:border-b-0 hover:bg-[#F8FAFC]/60"
            >
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="m-0 text-sm font-semibold text-[#0F172A]">
                    {row?.section || "Business change"}
                  </p>
                  <span
                    className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${statusTone(
                      row?.status,
                    )}`}
                  >
                    {row?.status || "Unknown"}
                  </span>
                </div>
                <p className="m-0 mt-1 text-[12px] text-[#6D7C94]">
                  {changes.length
                    ? `${changes.length} change${
                        changes.length === 1 ? "" : "s"
                      }${summary ? `: ${summary}${more}` : ""}`
                    : "No field changes listed"}
                  {when
                    ? ` · ${moment(when).format("DD MMM YYYY, HH:mm")}`
                    : ""}
                </p>
              </div>
              <button
                type="button"
                onClick={() => onOpenRequest?.(row)}
                className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-[#D6DEEA] bg-white px-3 text-sm font-semibold text-[#111827] hover:bg-[#F8FAFC]"
              >
                <Icon icon="mdi:eye-outline" width={16} height={16} />
                Open detail
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
